import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { ArrowLeft, ArrowRight, CalendarDate } from "react-bootstrap-icons";
import { DateRangePicker as DP, createStaticRanges } from "react-date-range";
import {
  calculateDateTimeRange,
  giveStaticRangeLabel,
} from "../../helpers/global";

const getRangePresets = (isFinancialDashboard = false) => {
  if (isFinancialDashboard) {
    return {
      "This Week": {
        startDate: moment()
          .startOf("week")
          .toDate(),
        endDate: moment()
          .endOf("week")
          .toDate(),
        rangeType: "Week",
        isPresetRange: true,
      },
      "Last Week": {
        startDate: moment()
          .subtract(1, "week")
          .startOf("week")
          .toDate(),
        endDate: moment()
          .subtract(1, "week")
          .endOf("week")
          .toDate(),
        rangeType: "Week",
        isPresetRange: true,
      },
      "This Month": {
        startDate: moment()
          .startOf("month")
          .toDate(),
        endDate: moment()
          .endOf("month")
          .toDate(),
        rangeType: "Month",
        isPresetRange: true,
      },
      "Last Month": {
        startDate: moment()
          .subtract(1, "month")
          .startOf("month")
          .toDate(),
        endDate: moment()
          .subtract(1, "month")
          .endOf("month")
          .toDate(),
        rangeType: "Month",
        isPresetRange: true,
      },
      "Rolling 1 Month": {
        startDate: moment()
          .subtract(1, "month")
          .startOf("day")
          .toDate(),
        endDate: moment()
          .endOf("day")
          .toDate(),
        rangeType: "Month",
        isPresetRange: true,
        description: "Most recent 30 days from today",
      },
      "Rolling 2 Months": {
        startDate: moment()
          .subtract(2, "months")
          .startOf("day")
          .toDate(),
        endDate: moment()
          .endOf("day")
          .toDate(),
        rangeType: "Month",
        isPresetRange: true,
        description: "Most recent 60 days from today",
      },
      "Rolling 3 Months": {
        startDate: moment()
          .subtract(3, "months")
          .startOf("day")
          .toDate(),
        endDate: moment()
          .endOf("day")
          .toDate(),
        rangeType: "Month",
        isPresetRange: true,
        description: "Most recent 90 days from today",
      },
      "Rolling 6 Months": {
        startDate: moment()
          .subtract(6, "months")
          .startOf("day")
          .toDate(),
        endDate: moment()
          .endOf("day")
          .toDate(),
        rangeType: "Month",
        isPresetRange: true,
        description: "Most recent 180 days from today",
      },
      "Rolling 12 Months": {
        startDate: moment()
          .subtract(12, "months")
          .startOf("day")
          .toDate(),
        endDate: moment()
          .endOf("day")
          .toDate(),
        rangeType: "Month",
        isPresetRange: true,
        description: "Most recent 365 days from today",
      },
    };
  }

  return {
    Today: {
      startDate: moment()
        .startOf("day")
        .toDate(),
      endDate: moment()
        .endOf("day")
        .toDate(),
    },
    "This Week": {
      startDate: moment()
        .startOf("week")
        .toDate(),
      endDate: moment()
        .endOf("week")
        .toDate(),
    },
    "Last Week": {
      startDate: moment()
        .subtract(1, "week")
        .startOf("week")
        .toDate(),
      endDate: moment()
        .subtract(1, "week")
        .endOf("week")
        .toDate(),
    },
    "This Month": {
      startDate: moment()
        .startOf("month")
        .toDate(),
      endDate: moment()
        .endOf("month")
        .toDate(),
    },
    "Last Month": {
      startDate: moment()
        .subtract(1, "month")
        .startOf("month")
        .toDate(),
      endDate: moment()
        .subtract(1, "month")
        .endOf("month")
        .toDate(),
    },
    "This Quarter": {
      startDate: moment()
        .startOf("quarter")
        .toDate(),
      endDate: moment()
        .endOf("quarter")
        .toDate(),
    },
    "Last Quarter": {
      startDate: moment()
        .subtract(1, "quarter")
        .startOf("quarter")
        .toDate(),
      endDate: moment()
        .subtract(1, "quarter")
        .endOf("quarter")
        .toDate(),
    },
    "This Year": {
      startDate: moment()
        .startOf("year")
        .toDate(),
      endDate: moment()
        .endOf("year")
        .toDate(),
    },
    "Last Year": {
      startDate: moment()
        .subtract(1, "year")
        .startOf("year")
        .toDate(),
      endDate: moment()
        .subtract(1, "year")
        .endOf("year")
        .toDate(),
    },
    "All Time": {
      startDate: null,
      endDate: null,
    },
  };
};

const DateRangePicker = ({
  value = {},
  onChange,
  className = "",
  isRTL,
  customView,
  FooterContent,
  closeOnSelect = true,
  allowNavigation = false,
  isFinancialDashboard = false,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [rangeType, setRangeType] = useState();

  const handleNavigateDate = (direction) => {
    const { startDate, endDate, presetLabel } = value;
    const matches = presetLabel?.match(/Rolling (\d+) Months?/);

    if (isFinancialDashboard && matches) {
      const numMonths = parseInt(matches[1], 10);
      const adjustment = direction === "Previous" ? -numMonths : numMonths;

      const newStartDate = moment(startDate)
        .add(adjustment, "months")
        .toDate();
      const newEndDate = moment(endDate)
        .add(adjustment, "months")
        .toDate();

      onChange({
        selection: {
          startDate: newStartDate,
          endDate: newEndDate,
          key: "selection",
          rangeType: value.rangeType,
          isPresetRange: true,
          presetLabel: presetLabel,
        },
      });
      return;
    }

    const { startDateTime, endDateTime } = calculateDateTimeRange(
      startDate,
      endDate,
      rangeType,
      direction
    );

    onChange({
      selection: {
        startDate: startDateTime,
        endDate: endDateTime,
        key: "selection",
        rangeType,
      },
    });
  };

  const dateRangeLabel = useMemo(() => {
    const startDate = moment(value?.startDate);
    const endDate = moment(value?.endDate);

    if (isFinancialDashboard && value?.startDate && value?.endDate) {
      const staticRangeLabel = giveStaticRangeLabel(
        value?.startDate,
        value?.endDate,
        getRangePresets(true)
      );
      if (staticRangeLabel) {
        return { label: staticRangeLabel, width: 180 };
      }

      return {
        label: `${startDate.format("DD MMM, YYYY")} - ${endDate.format(
          "DD MMM, YYYY"
        )}`,
        width: 160,
      };
    }

    switch (rangeType) {
      case "Day":
        return { label: startDate.format("DD MMM, YYYY"), width: 100 };
      case "Month":
        return { label: startDate.format("MMMM YYYY"), width: 100 };
      case "Quarter":
        return {
          label: `${startDate.format("MMM, YYYY")} - ${endDate.format(
            "MMM, YYYY"
          )}`,
          width: 160,
        };
      case "Year":
        return { label: startDate.format("YYYY"), width: 60 };
      case "Week":
        return {
          label: `${startDate.format("DD MMM, YYYY")} - ${endDate.format(
            "DD MMM, YYYY"
          )}`,
          width: 160,
        };
      default:
        return {
          label: `${startDate.format("DD MMM, YYYY")} - ${endDate.format(
            "DD MMM, YYYY"
          )}`,
          width: 160,
        };
    }
  }, [value, rangeType, isFinancialDashboard]);

  const staticRanges = useMemo(
    () =>
      createStaticRanges(
        Object.keys(getRangePresets(isFinancialDashboard)).map((key) => ({
          label: key,
          range: () => ({
            ...getRangePresets(isFinancialDashboard)[key],
            isPresetRange: true,
            presetLabel: key,
            rangeType: key,
          }),
          isSelected(range) {
            const definedRange = this.range();
            if (!definedRange.startDate || !definedRange.endDate) {
              return !range.startDate && !range.endDate;
            }
            return (
              moment(range.startDate).isSame(definedRange.startDate, "day") &&
              moment(range.endDate).isSame(definedRange.endDate, "day")
            );
          },
        }))
      ),
    [isFinancialDashboard]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dateRangePicker = document.getElementById(
        "custom-date-range-picker"
      );
      if (dateRangePicker && !dateRangePicker.contains(event.target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setRangeType(value?.rangeType);
  }, [value]);

  return (
    <div className={className} style={{ position: "relative" }}>
      {customView ? (
        customView({ openCalendar: () => !showPicker && setShowPicker(true) })
      ) : (
        <div className="d-flex gap-1 align-items-center">
          {allowNavigation && (
            <>
              <Button
                variant="outline-dark"
                className="d-flex align-items-center m-0"
                size="sm"
                disabled={rangeType === "All Time"}
                onClick={() => handleNavigateDate("Previous")}
              >
                <ArrowLeft size={15} />
              </Button>
              <Button
                variant="outline-dark"
                className="d-flex align-items-center"
                size="sm"
                disabled={rangeType === "All Time"}
                onClick={() => handleNavigateDate("Next")}
              >
                <ArrowRight size={15} />
              </Button>
            </>
          )}
          <Button
            variant="white"
            size="sm"
            className="d-flex border-dark justify-content-center align-items-center"
            onClick={() => !showPicker && setShowPicker(true)}
          >
            <CalendarDate size={15} />
            <div
              className="border-left border-dark ml-1"
              style={{ width: 2, height: 15 }}
            />
            <h6
              className="smallFont mb-0 flex-grow-1 text-center"
              style={{ width: 180 }}
            >
              {value.startDate && value.endDate
                ? dateRangeLabel?.label
                : "All Time"}
            </h6>
          </Button>{" "}
        </div>
      )}

      {showPicker && (
        <div
          className="border"
          id="custom-date-range-picker"
          style={{
            position: "absolute",
            top: "105%",
            right: isRTL ? -500 : 0,
            zIndex: 999,
          }}
        >
          <DP
            editableDateInputs={false}
            onChange={(range) => {
              let rangeSelection = range?.selection || {};

              let rangeType =
                rangeSelection?.rangeType?.split(" ").pop() || "Week";

              if (rangeType.endsWith("s")) {
                rangeType = rangeType.slice(0, -1);
              }

              let formattedRange =
                rangeType === "Today"
                  ? "Day"
                  : rangeType === "Time"
                  ? "All Time"
                  : rangeType;

              setRangeType(formattedRange);

              onChange({
                selection: {
                  ...rangeSelection,
                  rangeType: formattedRange,
                },
              });
              if (rangeSelection?.isPresetRange && closeOnSelect) {
                setShowPicker(false);
              }
            }}
            moveRangeOnFirstSelection={true}
            ranges={[value]}
            staticRanges={staticRanges}
            inputRanges={[]}
            showSelectionPreview={true}
            direction="vertical"
            footerContent={FooterContent && FooterContent({ setShowPicker })}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
